'use client'

import { apiClient, gameApi } from '@/lib/api'
import config from '@/lib/config'
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'

const AuthContext = createContext(undefined)

export function AuthProvider({ children }) {
  const [isLoading, setIsLoading] = useState(false)
  const [user, setUser] = useState({
    id: '',
    email: '',
    username: '',
    isAuthenticated: false,
    isLoading: true,
    profile: null,
    balance: 0
  })

  const authCheckInProgress = React.useRef(false)

  // Fetch user profile from Worker
  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await gameApi.user.getProfile()
      if (response.success && response.data) {
        const profile = response.data
        setUser(prev => ({
          ...prev,
          id: profile.id,
          email: profile.email,
          username: profile.username,
          isAuthenticated: true,
          isLoading: false,
          profile,
          balance: profile.balance
        }))
      } else {
        throw new Error('Failed to fetch profile')
      }
    } catch (error) {
      setUser(prev => ({ ...prev, isAuthenticated: false, isLoading: false, profile: null }))
    }
  }, [])

  // Check auth status via Worker cookie
  const checkAuthStatus = useCallback(async () => {
    if (authCheckInProgress.current) return
    try {
      authCheckInProgress.current = true
      setIsLoading(true)
      const res = await fetch(`${config.backendUrl}/api/auth/check`, { credentials: 'include' })
      if (res.ok) {
        await fetchUserProfile()
      } else {
        setUser(prev => ({ ...prev, isAuthenticated: false, isLoading: false, profile: null }))
      }
    } catch {
      setUser(prev => ({ ...prev, isAuthenticated: false, isLoading: false, profile: null }))
    } finally {
      setIsLoading(false)
      authCheckInProgress.current = false
    }
  }, [fetchUserProfile])

  // Email/password sign in → Worker
  const signIn = useCallback(async (email, password) => {
    try {
      setIsLoading(true)
      const res = await fetch(`${config.backendUrl}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      })
      const data = await res.json()
      if (!data.success) throw new Error(data.error || 'Sign in failed')
      await fetchUserProfile()
    } catch (error) {
      throw new Error(error.message || 'Sign in failed')
    } finally {
      setIsLoading(false)
    }
  }, [fetchUserProfile])

  // Registration → Worker
  const signUp = useCallback(async (email, password, metadata) => {
    try {
      setIsLoading(true)
      const username = metadata?.name || email.split('@')[0]
      const res = await fetch(`${config.backendUrl}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email,
          password,
          username,
          birthDate: metadata?.birthDate,
          country: metadata?.country,
        }),
      })
      const data = await res.json()
      if (!data.success) throw new Error(data.error || 'Sign up failed')
      await fetchUserProfile()
    } catch (error) {
      throw new Error(error.message || 'Sign up failed')
    } finally {
      setIsLoading(false)
    }
  }, [fetchUserProfile])

  // OTP login (stub — can be wired up later)
  const loginWithOTP = useCallback(async (email, code) => {
    throw new Error('OTP login not yet available')
  }, [])

  // OAuth (stub — redirect to login page for now)
  const signInWithGoogle = useCallback(async () => {
    throw new Error('Google sign-in not configured yet')
  }, [])

  const signInWithTwitter = useCallback(async () => {
    throw new Error('Twitter sign-in not configured yet')
  }, [])

  const signInWithMetaMask = useCallback(async () => {
    throw new Error('MetaMask sign-in not configured yet')
  }, [fetchUserProfile])

  // Sign out → Worker
  const signOut = useCallback(async () => {
    try {
      setIsLoading(true)
      await fetch(`${config.backendUrl}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      })
    } catch {} finally {
      setUser({ id: '', email: '', username: '', isAuthenticated: false, isLoading: false, profile: null, balance: 0 })
      setIsLoading(false)
      if (typeof window !== 'undefined') window.location.href = '/'
    }
  }, [])

  const refreshProfile = useCallback(async () => {
    if (user.isAuthenticated) await fetchUserProfile()
  }, [user.isAuthenticated, fetchUserProfile])

  const updateUser = useCallback((profile) => {
    setUser(prev => ({ ...prev, profile: prev.profile ? { ...prev.profile, ...profile } : null }))
  }, [])

  const updateBalance = useCallback((newBalance) => {
    setUser(prev => ({
      ...prev,
      balance: newBalance,
      profile: prev.profile ? { ...prev.profile, balance: newBalance } : null
    }))
  }, [])

  // Check auth on mount
  useEffect(() => {
    checkAuthStatus()
  }, [checkAuthStatus])

  const value = {
    user,
    isLoading,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithTwitter,
    signInWithMetaMask,
    signOut,
    refreshProfile,
    updateUser,
    updateBalance,
    fetchUserProfile,
    loginWithOTP,
    checkAuthStatus,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider')
  return context
}

export default AuthContext
